import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Link } from 'react-router';
import type { PermissionCode } from '@solar/shared';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireAuth, RequirePerm } from '@/auth/guards';
import { Spinner, EmptyState } from '@/components/ui/feedback';
import { Button } from '@/components/ui/button';
import LoginPage from '@/pages/LoginPage';
import ForceChangePasswordPage from '@/pages/ForceChangePasswordPage';

const HomePage = lazy(() => import('@/pages/HomePage'));
const NewsPage = lazy(() => import('@/pages/posts/NewsPage'));
const ProductsPage = lazy(() => import('@/pages/products/ProductsPage'));
const ProductFormPage = lazy(() => import('@/pages/products/ProductFormPage'));
const ManufacturersPage = lazy(() => import('@/pages/manufacturers/ManufacturersPage'));
const PackagesPage = lazy(() => import('@/pages/packages/PackagesPage'));
const PackageDetailPage = lazy(() => import('@/pages/packages/PackageDetailPage'));
const PackageOptionsPage = lazy(() => import('@/pages/packages/PackageOptionsPage'));
const FinancingPage = lazy(() => import('@/pages/financing/FinancingPage'));
const QuotesPage = lazy(() => import('@/pages/quotes/QuotesPage'));
const QuoteWizardPage = lazy(() => import('@/pages/quotes/QuoteWizardPage'));
const QuoteDetailPage = lazy(() => import('@/pages/quotes/QuoteDetailPage'));
const TemplatesPage = lazy(() => import('@/pages/templates/TemplatesPage'));
const TemplateDetailPage = lazy(() => import('@/pages/templates/TemplateDetailPage'));
const UsersPage = lazy(() => import('@/pages/users/UsersPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const AuditPage = lazy(() => import('@/pages/audit/AuditPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const StatsPage = lazy(() => import('@/pages/stats/StatsPage'));
const DesignStatsPage = lazy(() => import('@/pages/stats/DesignStatsPage'));
const StaffPage = lazy(() => import('@/pages/staff/StaffPage'));
const BookingPage = lazy(() => import('@/pages/booking/BookingPage'));
const IntegrationPage = lazy(() => import('@/pages/integration/IntegrationPage'));

const page = (el: ReactNode, perms?: PermissionCode[]) => (
  <Suspense fallback={<Spinner />}>{perms ? <RequirePerm perms={perms}>{el}</RequirePerm> : el}</Suspense>
);

function NotFound() {
  return (
    <EmptyState
      title="Không tìm thấy trang"
      description="Đường dẫn không tồn tại hoặc đã bị thay đổi."
      action={
        <Button asChild variant="outline">
          <Link to="/">Về trang chủ</Link>
        </Button>
      }
    />
  );
}

export const router = createBrowserRouter([
  { path: '/dang-nhap', element: <LoginPage /> },
  {
    path: '/doi-mat-khau',
    element: (
      <RequireAuth>
        <ForceChangePasswordPage />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: page(<HomePage />) },
      { path: 'tin-noi-bo', element: page(<NewsPage />, ['post.view', 'post.manage']) },
      { path: 'vat-tu', element: page(<ProductsPage />, ['product.view']) },
      { path: 'vat-tu/moi', element: page(<ProductFormPage />, ['product.manage']) },
      { path: 'vat-tu/:id', element: page(<ProductFormPage />, ['product.view']) },
      {
        path: 'hang-san-xuat',
        element: page(<ManufacturersPage />, ['product.view', 'manufacturer.manage', 'compatibility.manage']),
      },
      { path: 'goi-san-pham', element: page(<PackagesPage />, ['package.view', 'quote.create']) },
      { path: 'goi-san-pham/danh-sach', element: page(<PackageOptionsPage />, ['package.view']) },
      { path: 'goi-san-pham/:id', element: page(<PackageDetailPage />, ['package.view', 'quote.create']) },
      { path: 'tai-chinh', element: page(<FinancingPage />, ['financing.view', 'financing.manage']) },
      {
        path: 'bao-gia',
        element: page(<QuotesPage />, ['quote.create', 'quote.view_all', 'quote.view_team']),
      },
      { path: 'bao-gia/moi', element: page(<QuoteWizardPage />, ['quote.create']) },
      {
        path: 'bao-gia/:id',
        element: page(<QuoteDetailPage />, ['quote.create', 'quote.view_all', 'quote.view_team']),
      },
      {
        path: 'bao-gia/:id/sua',
        element: page(<QuoteWizardPage />, ['quote.create', 'quote.manage_all', 'quote.manage_team']),
      },
      { path: 'thong-ke', element: page(<StatsPage />, ['quote.stats', 'quote.view_all']) },
      { path: 'thong-ke-thiet-ke', element: page(<DesignStatsPage />, ['design.stats']) },
      { path: 'mau-bao-gia', element: page(<TemplatesPage />, ['template.manage']) },
      { path: 'mau-bao-gia/:id', element: page(<TemplateDetailPage />, ['template.manage']) },
      { path: 'nhan-vien', element: page(<UsersPage />, ['user.manage', 'role.manage']) },
      { path: 'cai-dat', element: page(<SettingsPage />, ['settings.manage']) },
      { path: 'nhat-ky', element: page(<AuditPage />, ['audit.view']) },
      { path: 'tai-khoan', element: page(<AccountPage />) },
      {
        path: 'nhan-su',
        element: page(<StaffPage />, ['staff.view', 'staff.manage', 'score.view', 'score.manage']),
      },
      { path: 'lich-ky-thuat', element: page(<BookingPage />, ['booking.use', 'booking.manage']) },
      { path: 'lich-ky-thuat/:tab', element: page(<BookingPage />, ['booking.use', 'booking.manage']) },
      { path: 'tich-hop', element: page(<IntegrationPage />, ['integration.manage']) },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
